import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import EmailPreview from '../components/email/EmailPreview';
import OrderConfirmationEmail from '../emails/templates/OrderConfirmationEmail';
import OrderConfirmationEmailV2 from '../emails/templates/OrderConfirmationEmailV2';
import PurchaseConfirmationEmail from '../emails/templates/PurchaseConfirmationEmail';
import ClassConfirmationEmail from '../emails/templates/ClassConfirmationEmail';
import ClassDateChangeNotificationEmail from '../emails/templates/ClassDateChangeNotificationEmail';
import ClassCancellationNotificationEmail from '../emails/templates/ClassCancellationNotificationEmail';
import InstructorChangeNotificationEmail from '../emails/templates/InstructorChangeNotificationEmail';
import TimeChangeNotificationEmail from '../emails/templates/TimeChangeNotificationEmail';
import InstructorCheckoutReminderEmail from '../emails/templates/InstructorCheckoutReminderEmail';
import CertificateDeliveryEmail from '../emails/templates/CertificateDeliveryEmail';
import InvoiceEmail from '../emails/templates/InvoiceEmail';
import SubscriptionCancellationEmail from '../emails/templates/SubscriptionCancellationEmail';
import WelcomeEmail from '../emails/templates/WelcomeEmail';
import PasswordResetEmail from '../emails/templates/PasswordResetEmail';
import SubscriptionUpdateEmail from '../emails/templates/SubscriptionUpdateEmail';
import NewUserRegistrationEmail from '../emails/templates/NewUserRegistrationEmail';
import ExamVoucherDeliveryEmail from '../emails/templates/ExamVoucherDeliveryEmail';
import { renderEmailToHtml } from '../emails/renderEmail';
import { supabase } from '../lib/supabase';
import { getEdgeFunctionUrl } from '../config/environment';
import { useAuthContext } from '../contexts/AuthContext';
import { companyConfig } from '../emails/config/colors';
import { Helmet } from 'react-helmet-async';

// Email protection configuration
const APP_ENV = import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'production';
const isDevelopment = APP_ENV === 'development';
const isStaging = APP_ENV === 'staging';
const TEST_EMAIL = 'teste.csm@trivium.com.br';
import { 
  Mail, 
  Settings, 
  Eye, 
  Code, 
  Send, 
  Copy, 
  Download, 
  Sparkles,
  FileText,
  Calendar,
  Bell,
  CreditCard,
  Package,
  AlertTriangle,
  Shield,
  Gift,
  Zap,
  RefreshCcw,
  Palette,
  Wand2,
  MailOpen,
  Play,
  Pause,
  ChevronDown,
  Award,
  Clock,
  ExternalLink
} from 'lucide-react';

// Organização dos templates por entidades
interface TemplateInfo {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  component: React.ComponentType<any>;
  props: any;
}

interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  templates: TemplateInfo[];
}

const ReactEmailPreview = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const { user } = useAuthContext();
  
  // Estado para o template selecionado
  const [selectedTemplate, setSelectedTemplate] = useState<string>('order-confirmation');
  const [selectedCategory, setSelectedCategory] = useState<string>('orders');
  
  // Estado para controle da interface
  const [activeView, setActiveView] = useState<'preview' | 'html'>('preview');
  
  // Definição das categorias e templates organizados
  const templateCategories: TemplateCategory[] = [
    {
      id: 'orders',
      name: 'Pedidos & Compras',
      description: 'Templates relacionados a confirmações de pedidos e compras',
      icon: Package,
      color: 'blue',
      templates: [
        {
          id: 'order-confirmation',
          name: 'Confirmação de Pedido',
          description: 'Email de confirmação após realização de pedido',
          icon: Package,
          color: 'blue',
          component: OrderConfirmationEmail,
          props: {
    customerName: "João Silva",
    items: [
      {
        planName: "Plano Profissional",
        planDescription: "Acesso completo a todas as funcionalidades premium",
        price: "R$ 99,95",
        billingPeriod: "Mensal",
        nextBillingDate: "14 Abr, 2025",
              }
    ],
    transactionId: "2342",
    purchaseDate: "14 Mar, 2025",
    email: "joao.silva@email.com",
    total: "R$ 149,90"
          }
        },
        {
          id: 'order-confirmation-v2',
          name: 'Confirmação de Pedido V2',
          description: 'Versão atualizada do email de confirmação de pedido',
          icon: Package,
          color: 'purple',
          component: OrderConfirmationEmailV2,
          props: {
    customerName: "João Silva",
    items: [
      {
        planName: "Plano Profissional",
        planDescription: "Acesso completo a todas as funcionalidades premium",
        price: "R$ 99,95",
        billingPeriod: "Mensal",
        nextBillingDate: "14 Abr, 2025",
              }
    ],
    transactionId: "2342",
    purchaseDate: "14 Mar, 2025",
    email: "joao.silva@email.com",
    total: "R$ 149,90",
    companyName: "Trivium"
          }
        },
        {
          id: 'purchase-confirmation',
          name: 'Confirmação de Compra',
          description: 'Email de confirmação de compra realizada',
          icon: CreditCard,
          color: 'green',
          component: PurchaseConfirmationEmail,
          props: {
    name: 'João Silva',
    company: 'SaaS Trivium',
    orderNumber: 'ORD-12345',
    orderDate: '10/06/2023',
    items: [
      { name: 'Plano Premium', quantity: 1, price: 399.00 },
      { name: 'Addon Integrações', quantity: 1, price: 99.00 },
    ],
    total: 498.00,
    paymentMethod: 'Cartão de Crédito',
    deliveryMethod: 'Acesso Digital',
    supportEmail: 'suporte@saasTrivium.com',
          }
        },
        {
          id: 'invoice',
          name: 'Fatura',
          description: 'Email de envio de fatura',
          icon: FileText,
          color: 'indigo',
          component: InvoiceEmail,
          props: {
            name: 'João Silva',
            company: 'Trivium',
            supportEmail: 'suporte@trivium.com.br',
            domain: 'trivium.com.br',
            invoice: {
              invoiceNumber: 'INV-2023-001',
              date: '01/08/2023',
              dueDate: '15/08/2023',
              amount: 'R$ 99,90',
              status: 'Pendente',
              items: [
                {
                  description: 'Assinatura Premium - Mensal',
                  quantity: 1,
                  unitPrice: 'R$ 99,90',
                  total: 'R$ 99,90'
                }
              ]
            },
            downloadLink: 'https://trivium.com.br/fatura/INV-2023-001/download',
            paymentLink: 'https://trivium.com.br/fatura/INV-2023-001/pagamento',
            accountLink: 'https://trivium.com.br/minha-conta/faturas',
          }
        }
      ]
    },
    {
      id: 'classes',
      name: 'Aulas & Formações',
      description: 'Templates para gestão de aulas e formações',
      icon: Calendar,
      color: 'purple',
      templates: [
        {
          id: 'class-confirmation-v5',
          name: 'Confirmação de Aula V5',
          description: 'Confirmação de inscrição em aula',
          icon: Calendar,
          color: 'purple',
          component: ClassConfirmationEmail,
          props: {
    userName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    courseDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    instructor: "Ana Carolina Soares",
    modality: "Híbrido",
    courseId: "FA-2025-001",
    session: "1ª aula",
    time: "06:00 às 14:00",
    address: "Av. Paulista, 1000 - Sala 203, São Paulo - SP",
    meetingLink: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_abc123@thread.v2/",
    companyName: "Empresa XYZ Ltda",
          }
        },
        {
          id: 'class-date-change',
          name: 'Alteração de Data/Horário',
          description: 'Notificação de mudança de data ou horário',
          icon: RefreshCcw,
          color: 'orange',
          component: ClassDateChangeNotificationEmail,
          props: {
    userName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    oldCourseDate: "15 de abril de 2025",
    oldTime: "06:00 às 14:00",
    newCourseDate: "22 de abril de 2025",
    newTime: "08:00 às 16:00",
    courseDuration: "120 minutos",
    instructor: "Ana Carolina Soares",
    modality: "Híbrido",
    courseId: "FA-2025-001",
    session: "1ª aula",
    address: "Av. Paulista, 1000 - Sala 203, São Paulo - SP",
    meetingLink: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_abc123@thread.v2/",
    companyName: "Empresa XYZ Ltda",
          }
        },
        {
          id: 'class-cancellation-notification',
          name: 'Cancelamento de Formação',
          description: 'Notificação de cancelamento de formação completa',
          icon: AlertTriangle,
          color: 'red',
          component: ClassCancellationNotificationEmail,
          props: {
    userName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    courseDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    instructor: "Ana Carolina Soares",
    modality: "Híbrido",
    courseId: "FA-2025-001",
    session: "1ª aula",
    time: "06:00 às 14:00",
    address: "Av. Paulista, 1000 - Sala 203, São Paulo - SP",
    companyName: "Empresa XYZ Ltda",
    cancellationReason: "Indisponibilidade do instrutor por motivos de saúde",
    refundInfo: "O valor será estornado automaticamente em até 5 dias úteis",
    rescheduleLink: "https://trivium.com.br/reagendar",
    supportEmail: "suporte@trivium.com.br",
    supportPhone: "(11) 3181-5940",
          }
        },
        {
          id: 'instructor-change',
          name: 'Alteração de Instrutor',
          description: 'Notificação de mudança de instrutor',
          icon: Sparkles,
          color: 'blue',
          component: InstructorChangeNotificationEmail,
          props: {
    userName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    courseDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    oldInstructor: "Ana Carolina Soares",
    newInstructor: "Dr. Roberto Martins",
    modality: "Híbrido",
    courseId: "FA-2025-001",
    session: "1ª aula",
    time: "06:00 às 14:00",
    address: "Av. Paulista, 1000 - Sala 203, São Paulo - SP",
    meetingLink: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_abc123@thread.v2/",
    companyName: "Empresa XYZ Ltda",
    changeReason: "Indisponibilidade por compromissos acadêmicos",
    newInstructorBio: "Especialista em Data Science com 15 anos de experiência em análise de dados e machine learning",
    newInstructorExperience: "PhD em Ciência da Computação, ex-Google, autor de 3 livros sobre Analytics",
    supportEmail: "suporte@trivium.com.br",
    supportPhone: "(11) 3181-5940",
          }
        },
        {
          id: 'time-change',
          name: 'Alteração de Horário',
          description: 'Notificação de mudança de horário',
          icon: Clock,
          color: 'yellow',
          component: TimeChangeNotificationEmail,
          props: {
    userName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    courseDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    instructor: "Ana Carolina Soares",
    modality: "Híbrido",
    courseId: "FA-2025-001",
    session: "1ª aula",
    oldTime: "06:00 às 14:00",
    newTime: "08:00 às 16:00",
    address: "Av. Paulista, 1000 - Sala 203, São Paulo - SP",
    meetingLink: "https://teams.microsoft.com/l/meetup-join/19%3ameeting_abc123@thread.v2/",
    companyName: "Empresa XYZ Ltda",
    changeReason: "Otimização da agenda para melhor aproveitamento",
    supportEmail: "suporte@trivium.com.br",
    supportPhone: "(11) 3181-5940",
          }
        },
        {
          id: 'instructor-checkout-reminder',
          name: 'Lembrete Check-out Instrutor',
          description: 'Lembrete para instrutor fazer check-out',
          icon: Bell,
          color: 'purple',
          component: InstructorCheckoutReminderEmail,
          props: {
    instructorName: "Ana Carolina Soares",
    courseName: "Fundamentos de Data Analytics",
    courseDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    courseId: "FA-2025-001",
    session: "1ª aula",
    time: "06:00 às 14:00",
    participantCount: 12,
    companyName: "Empresa XYZ Ltda",
    checkoutDeadline: "16 de abril de 2025 às 18:00",
    checkoutLink: "https://trivium.com.br/instructor/checkout/FA-2025-001",
    supportEmail: "suporte@trivium.com.br",
    supportPhone: "(11) 3181-5940",
    managerName: "Carlos Silva",
    managerEmail: "carlos.silva@trivium.com.br",
          }
        }
      ]
    },
    {
      id: 'certificates',
      name: 'Certificados & Vouchers',
      description: 'Templates para entrega de certificados e vouchers',
      icon: Award,
      color: 'green',
      templates: [
        {
          id: 'certificate-delivery',
          name: 'Entrega de Certificado',
          description: 'Email de entrega de certificado de conclusão',
          icon: Award,
          color: 'green',
          component: CertificateDeliveryEmail,
          props: {
    participantName: "João Silva",
    courseName: "Fundamentos de Data Analytics",
    completionDate: "15 de abril de 2025",
    courseDuration: "120 minutos",
    instructorName: "Ana Carolina Soares",
    courseId: "FA-2025-001",
    certificateId: "CERT-FA-2025-001-JS",
    certificateUrl: "https://trivium.com.br/certificates/CERT-FA-2025-001-JS",
    verificationUrl: "https://trivium.com.br/verify/CERT-FA-2025-001-JS",
    companyName: "Empresa XYZ Ltda",
    participantEmail: "joao.silva@empresa.com",
    hoursCompleted: "8 horas",
    validUntil: "15 de abril de 2026",
    achievementLevel: "Aprovado com Excelência",
    supportEmail: "suporte@trivium.com.br",
    supportPhone: "(11) 3181-5940",
    congratulationsMessage: "Parabéns pela conclusão da sua formação! Seu certificado está pronto.",
          }
        },
        {
          id: 'exam-voucher-delivery',
          name: 'Envio de Voucher de Exame',
          description: 'Email de entrega de voucher para exames',
          icon: Gift,
          color: 'blue',
          component: ExamVoucherDeliveryEmail,
          props: {
            userName: "João Silva",
            examName: "Microsoft Azure Fundamentals (AZ-900)",
            voucherCode: "AZ900-2025-ABC123",
            examProvider: "Microsoft",
            examDuration: "60 minutos",
            validUntil: "31 de dezembro de 2025",
            examLanguage: "Português (Brasil)",
            examFormat: "Online Proctored",
            schedulingInstructions: "Acesse o portal da Microsoft e agende seu exame usando o código do voucher fornecido",
            schedulingLink: "https://docs.microsoft.com/learn/certifications/exams/az-900",
            companyName: "Empresa XYZ Ltda",
            supportEmail: "suporte@trivium.com.br",
            supportPhone: "(11) 3181-5940",
          }
        }
      ]
    },
    {
      id: 'subscriptions',
      name: 'Assinaturas',
      description: 'Templates relacionados a assinaturas e planos',
      icon: Zap,
      color: 'yellow',
      templates: [
        {
          id: 'subscription-update',
          name: 'Atualização de Assinatura',
          description: 'Notificação de mudanças na assinatura',
          icon: Zap,
          color: 'yellow',
          component: SubscriptionUpdateEmail,
          props: {
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
            subscription: {
              updateType: 'increase',
              updateDate: '15/08/2023',
              effectiveDate: '15/08/2023',
              planChanges: [
                {
                  planName: 'Licença Profissional',
                  oldQuantity: 5,
                  newQuantity: 10,
                  pricePerUnit: 'R$ 99,90',
                  oldTotal: 'R$ 499,50',
                  newTotal: 'R$ 999,00'
                }
              ],
              newBillingTotal: 'R$ 1.198,60',
              nextBillingDate: '15/09/2023'
    },
    accountLink: 'https://trivium.com.br/minha-conta',
            invoiceLink: 'https://trivium.com.br/minha-conta/faturas/atual',
          }
        },
        {
          id: 'subscription-cancellation',
          name: 'Cancelamento de Assinatura',
          description: 'Confirmação de cancelamento de assinatura',
          icon: Package,
          color: 'red',
          component: SubscriptionCancellationEmail,
          props: {
    name: 'João Silva',
    company: 'Trivium',
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
    subscription: {
      plan: 'Plano Premium',
      endDate: '15/08/2023',
      accessUntil: '31/08/2023'
    },
    reactivateLink: 'https://trivium.com.br/minha-conta/reativar',
    feedbackLink: 'https://trivium.com.br/feedback/cancelamento',
          }
        }
      ]
    },
    {
      id: 'users',
      name: 'Usuários & Autenticação',
      description: 'Templates para gestão de usuários e autenticação',
      icon: Shield,
      color: 'slate',
      templates: [
        {
          id: 'welcome',
          name: 'Boas-vindas',
          description: 'Email de boas-vindas para novos usuários',
          icon: Sparkles,
          color: 'pink',
          component: WelcomeEmail,
          props: {
    name: 'João Silva',
    company: 'Trivium',
    email: 'joao.silva@email.com',
    activationLink: 'https://trivium.com.br/activate/abc123',
    supportEmail: 'suporte@trivium.com.br',
    planName: 'Plano Profissional',
    domain: 'trivium.com.br',
          }
        },
        {
          id: 'password-reset',
          name: 'Redefinição de Senha',
          description: 'Email para redefinição de senha',
          icon: Shield,
          color: 'slate',
          component: PasswordResetEmail,
          props: {
    name: 'João Silva',
    company: 'Trivium',
    resetLink: 'https://trivium.com.br/reset-password/abc123',
    expirationHours: 24,
    supportEmail: 'suporte@trivium.com.br',
    domain: 'trivium.com.br',
          }
        },
        {
          id: 'new-user-registration',
          name: 'Novo Usuário Cadastrado',
          description: 'Notificação de novo usuário cadastrado no sistema',
          icon: Calendar,
          color: 'purple',
          component: NewUserRegistrationEmail,
          props: {
    user_id: 'USR-12345',
    email: 'joao.silva@empresa.com.br',
    user_name: 'João Silva',
    registration_date: '2025-01-15T10:30:00Z',
    org_name: 'Empresa XYZ Ltda',
    domain: 'empresaxyz.com.br',
    industry: 'Tecnologia',
    subscription_id: 'SUB-789',
    plan_name: 'Plano Profissional',
    plan_type: 'premium',
    subscription_start_date: '2025-01-15',
    subscription_end_date: '2025-12-31',
    activation_link: 'https://trivium.com.br/activate/abc123',
    dashboard_link: 'https://trivium.com.br/dashboard',
    support_link: 'https://trivium.com.br/support',
    welcome_message: 'Seja bem-vindo à nossa plataforma de gestão educacional!',
    next_steps: [
      'Ative sua conta clicando no link de ativação',
      'Configure seu perfil e preferências',
      'Explore os recursos disponíveis em seu plano',
      'Convide sua equipe para colaborar'
    ],
    created_by: 'ADMIN-001',
    created_by_name: 'Maria Administradora',
    created_by_email: 'admin@empresaxyz.com.br',
          }
        }
      ]
    }
  ];
  
  // Funções auxiliares para encontrar templates e categorias
  const findTemplate = (templateId: string): TemplateInfo | undefined => {
    for (const category of templateCategories) {
      const template = category.templates.find(t => t.id === templateId);
      if (template) return template;
    }
    return undefined;
  };

  const findCategory = (categoryId: string): TemplateCategory | undefined => {
    return templateCategories.find(c => c.id === categoryId);
  };

  const getCurrentTemplate = (): TemplateInfo | undefined => {
    return findTemplate(selectedTemplate);
  };

  const getCurrentCategory = (): TemplateCategory | undefined => {
    return findCategory(selectedCategory);
  };
  
  // Estado para o HTML exportado
  const [exportedHtml, setExportedHtml] = useState<string>('');
  
  // Manipuladores de eventos
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    setExportedHtml('');
    
    // Encontrar a categoria do template selecionado
    for (const category of templateCategories) {
      if (category.templates.some(t => t.id === templateId)) {
        setSelectedCategory(category.id);
        break;
      }
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    const category = findCategory(categoryId);
    if (category && category.templates.length > 0) {
      setSelectedTemplate(category.templates[0].id);
    setExportedHtml('');
    }
  };
  
  // Exportar o HTML do template atual
  const handleExportHtml = () => {
    const template = getCurrentTemplate();
    if (template) {
      const html = renderEmailToHtml(template.component, template.props);
    setExportedHtml(html);
    }
  };
  
  // Copiar HTML para a área de transferência
  const copyHtmlToClipboard = () => {
    navigator.clipboard.writeText(exportedHtml);
    toast({
      title: 'Sucesso',
      description: 'HTML copiado para a área de transferência!',
    });
  };

  // Abrir preview em nova janela
  const openPreviewInNewWindow = (template: TemplateInfo) => {
    const htmlContent = renderEmailToHtml(template.component, template.props);
    
    // Cria uma nova janela
    const newWindow = window.open('', '_blank', 'width=800,height=900,scrollbars=yes,resizable=yes');
    
    if (newWindow) {
      // Escreve o HTML do email na nova janela
      newWindow.document.write(`
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Preview: ${template.name} - Trivium</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #f8fafc;
            }
            .preview-header {
              background: white;
              padding: 16px 20px;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              margin-bottom: 20px;
              border-left: 4px solid #3b82f6;
            }
            .preview-title {
              margin: 0;
              color: #1f2937;
              font-size: 18px;
              font-weight: 600;
            }
            .preview-subtitle {
              margin: 4px 0 0 0;
              color: #6b7280;
              font-size: 14px;
            }
            .email-container {
              background: white;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <div class="preview-header">
            <h1 class="preview-title">${template.name}</h1>
            <p class="preview-subtitle">${template.description}</p>
          </div>
          <div class="email-container">
            ${htmlContent}
          </div>
        </body>
        </html>
      `);
      newWindow.document.close();
      
      toast({
        title: 'Preview Aberto',
        description: 'Template aberto em nova janela!',
      });
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível abrir nova janela. Verifique se pop-ups estão bloqueados.',
        variant: 'destructive',
      });
    }
  };

  // Função para enviar um email de teste
  const sendTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: 'Erro',
        description: 'Por favor, informe um email para teste.',
        variant: 'destructive',
      });
      return;
    }
    
    const template = getCurrentTemplate();
    if (!template) {
      toast({
        title: 'Erro',
        description: 'Template não encontrado.',
        variant: 'destructive',
      });
      return;
    }
    
    const finalEmail = testEmail;
    const environment = isDevelopment ? 'DEV' : isStaging ? 'HOM' : 'PROD';
    
    setIsLoading(true);
    
    try {
      // Gerar o HTML do template
      const htmlContent = renderEmailToHtml(template.component, template.props);

      // Determinar subject com base no template selecionado
      let subject = `[${environment}] ${template.name} - Teste`;

      // Chamar Edge Function para enviar o email
      const { data: supabaseConfig } = await supabase.auth.getSession();
      const supabaseKey = supabaseConfig.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${getEdgeFunctionUrl('resend-send-email')}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: "Trivium • Microsoft Partner <naoresponda@notificacoes.trivium.com.br>",
          to: [finalEmail],
          subject: subject,
          html: htmlContent,
          reply_to: ["atendimento@trivium.com.br"]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro na Edge Function: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      
      if (result.message === "Email enviado com sucesso" && result.result?.data?.id) {
        toast({
          title: 'Sucesso',
          description: `Email de teste enviado com sucesso para ${finalEmail}!`,
        });
      } else {
        console.error('Unexpected response format:', result);
        throw new Error(`Falha no envio do email: ${JSON.stringify(result)}`);
      }

    } catch (error) {
      console.error('Erro ao enviar email de teste:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o email de teste.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentTemplate = getCurrentTemplate();
  const currentCategory = getCurrentCategory();

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Templates de Email</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header */}
      <div 
        className="relative overflow-hidden" 
        style={{ 
          backgroundColor: '#1F1746',
          backgroundImage: 'url(/elemento-01.svg)',
          backgroundSize: '200%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Templates de Email</h1>
                  <p className="text-blue-100 text-base pb-2">
                      Visualize e teste seus templates organizados por categoria
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={handleExportHtml}
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar HTML
              </Button>
              <Button 
                size="default" 
                onClick={sendTestEmail}
                disabled={isLoading}
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-gray-900 rounded-full border-t-transparent mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Teste
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
          {/* Template Selection by Categories */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="min-w-0 flex-1">
                  <Label htmlFor="test-email" className="text-sm font-extrabold mb-2 block">
                    Email para Teste
                  </Label>
                  <Input 
                    id="test-email" 
                    type="email" 
                    placeholder="seu@email.com" 
                    value={testEmail} 
                    onChange={(e) => setTestEmail(e.target.value)} 
                    className="h-12 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                </div>
                
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveView('preview')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeView === 'preview'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                <button
                  onClick={() => setActiveView('html')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeView === 'html'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Code className="h-4 w-4" />
                  HTML
                </button>
                </div>
              </div>

              {/* Categories Tabs */}
              <Tabs value={selectedCategory} onValueChange={handleCategoryChange} className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-6">
                  {templateCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    return (
                      <TabsTrigger 
                        key={category.id} 
                        value={category.id}
                        className="flex items-center gap-2 text-xs font-extrabold"
                      >
                        <CategoryIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">{category.name}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {templateCategories.map((category) => (
                  <TabsContent key={category.id} value={category.id} className="space-y-4">
                    <div className="text-center mb-6">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 bg-${category.color}-50 text-${category.color}-700 rounded-full border border-${category.color}-200`}>
                        <category.icon className="h-4 w-4" />
                        <span className="font-extrabold">{category.name}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{category.description}</p>
                    </div>

                    {/* Templates Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {category.templates.map((template) => {
                        const TemplateIcon = template.icon;
                        const isSelected = selectedTemplate === template.id;
                        
                        return (
                          <Card 
                            key={template.id}
                            className={`group cursor-pointer transition-all duration-200 hover:shadow-lg ${
                              isSelected 
                                ? `border-${template.color}-500 bg-${template.color}-50 shadow-md` 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleTemplateChange(template.id)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 bg-${template.color}-100 rounded-lg flex-shrink-0`}>
                                  <TemplateIcon className={`h-5 w-5 text-${template.color}-600`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <h3 className="font-extrabold text-gray-900 text-sm mb-1 truncate">
                                        {template.name}
                                      </h3>
                                      <p className="text-xs text-gray-600 line-clamp-2">
                                        {template.description}
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-8 w-8 p-0 hover:bg-gray-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPreviewInNewWindow(template);
                                      }}
                                      title="Abrir preview em nova janela"
                                    >
                                      <ExternalLink className="h-4 w-4 text-gray-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Preview Panel */}
          {currentTemplate && (
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3">
                {activeView === 'preview' ? (
                  <>
                      <Eye className="h-5 w-5" />
                      Preview do Template
                  </>
                ) : (
                  <>
                      <Code className="h-5 w-5" />
                    Código HTML
                  </>
                )}
                  <Badge className={`bg-${currentTemplate.color}-100 text-${currentTemplate.color}-700 border-${currentTemplate.color}-200`}>
                    {currentTemplate.name}
                  </Badge>
              </CardTitle>
              <CardDescription>
                {activeView === 'preview' 
                    ? currentTemplate.description 
                  : 'Código HTML gerado do template'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
                {activeView === 'preview' ? (
                  <div className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-visible">
                    <div className="p-4">
                      <EmailPreview 
                        EmailComponent={currentTemplate.component} 
                        props={currentTemplate.props} 
                      />
                    </div>
                  </div>
                ) : (
                <div className="space-y-4">
                {exportedHtml ? (
                    <>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            HTML Gerado
                          </Badge>
                          <span className="text-sm text-gray-600">
                            {exportedHtml.length} caracteres
                          </span>
                        </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={copyHtmlToClipboard}
                          className="flex items-center gap-2"
                      >
                          <Copy className="h-4 w-4" />
                          Copiar
                      </Button>
                    </div>
                      <div className="border border-gray-200 rounded-lg bg-gray-900 p-4 max-h-[500px] overflow-auto">
                        <pre className="text-xs text-green-400 font-mono leading-relaxed">
                      {exportedHtml}
                    </pre>
                  </div>
                    </>
                  ) : (
                    <div className="text-center py-20">
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                          <Code className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-extrabold text-gray-900">
                            HTML não gerado
                          </h3>
                          <p className="text-gray-500 max-w-md mx-auto">
                            Clique em "Exportar HTML" para gerar e visualizar o código do template.
                          </p>
                        </div>
                        <Button onClick={handleExportHtml} size="lg">
                          <Wand2 className="h-5 w-5 mr-2" />
                          Gerar HTML
                        </Button>
                      </div>
                  </div>
                )}
                </div>
              )}
          </CardContent>
        </Card>
          )}
        
        {/* Bottom Spacing */}
        <div className="h-16" />
        </div>
      </div>
    </>
  );
};

export default ReactEmailPreview; 